import { formatVenueDate, formatVenueTime } from '../../../utils/venueTime';
import { fieldStyle } from '../../dashboards/primitives';
import { Field } from '../../dashboards/venues/venueCreateFields';
import { friendlyZoneName } from '../friendlyTimeZone';

import { requiredProps } from './step-layout';
import type { LocationFormApi } from './when-what-form-api';

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

function readableWhen(fields: LocationFormApi['fields']): string {
  if (!fields.date || !fields.startTime) return 'the time above';
  const local = `${fields.date}T${fields.startTime}:00`;
  return `${formatVenueTime(local)} on ${formatVenueDate(local)}`;
}

function suggestionSource(mode: LocationFormApi['fields']['locationMode']): string {
  return mode === 'tbd' ? 'the most common zone for this region’s venues' : 'the state';
}

function guidance(fields: LocationFormApi['fields']): string {
  const zone = fields.timeZone ? friendlyZoneName(fields.timeZone) : null;
  const tbd = fields.locationMode === 'tbd';
  if (!zone) {
    return tbd
      ? 'The location isn’t decided yet, so choose the time zone the date and time are in.'
      : 'Pick the state to suggest a time zone, or choose one below.';
  }
  const when = readableWhen(fields);
  if (fields.timeZoneSource === 'manual') {
    return `You chose ${zone}, so ${when} is saved in ${zone}.`;
  }
  const lead = tbd ? 'The location isn’t decided yet. ' : '';
  return `${lead}${when} is ${zone}, suggested from ${suggestionSource(fields.locationMode)}. Confirm it, or choose another.`;
}

/**
 * The event's time zone, spelled out in plain English so the RD can catch a
 * default that is an hour off (Knoxville, the FL panhandle and west Texas all
 * differ from their state's dominant zone). Shown for a client address and for
 * a to-be-determined event; a partner venue carries its own zone.
 */
export default function TimeZoneConfirmation({ form }: { form: LocationFormApi }) {
  const { fields, setTimeZone, fieldError } = form;
  const error = fieldError('timeZone');
  const zones =
    fields.timeZone && !TIMEZONE_OPTIONS.includes(fields.timeZone)
      ? [...TIMEZONE_OPTIONS, fields.timeZone]
      : TIMEZONE_OPTIONS;
  return (
    <Field
      label="Event time zone"
      required
      error={error}
      errorId="private-event-time-zone-error"
      linkControl={false}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 10,
          borderRadius: 8,
          background: 'var(--nb-warn-soft)',
          border: '1px solid var(--nb-border-2)',
        }}
      >
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--nb-ink)' }}>
          {guidance(fields)}
        </p>
        <select
          aria-label="Event time zone"
          {...requiredProps(error)}
          aria-describedby={error ? 'private-event-time-zone-error' : undefined}
          value={fields.timeZone}
          onChange={(e) => setTimeZone(e.target.value)}
          style={fieldStyle(error ? 'error' : undefined)}
        >
          <option value="">Select a time zone…</option>
          {zones.map((tz) => (
            <option key={tz} value={tz}>
              {friendlyZoneName(tz)}
            </option>
          ))}
        </select>
      </div>
    </Field>
  );
}
